import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Phone } from 'lucide-react';
import { supabase } from '../../services/supabaseClient';
import { serviceRoleClient } from '../../services/serviceRoleClient';
import CallRecordingPlayer from '../CallRecordingPlayer';
import VoiceAiCallRecording from '../VoiceAiCallRecording';

const db = () => serviceRoleClient || supabase;

const last10 = (phone) => {
  const d = String(phone || '').replace(/\D/g, '');
  return d.length >= 10 ? d.slice(-10) : d;
};

const SARV_BASE = 'https://s-ct3.sarv.com/v2/recording/direct';
const SARV_IDS = ['11965974', '68798380'];

const parseJson = (value, fallback) => {
  try {
    if (value == null) return fallback;
    if (typeof value === 'string') {
      const s = value.trim();
      if (!s) return fallback;
      return JSON.parse(s);
    }
    return value;
  } catch {
    return fallback;
  }
};

const buildSarvUrl = (file, sarvUserId) => {
  if (!file || typeof file !== 'string') return null;
  const path = file.trim();
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const userId = String(sarvUserId || SARV_IDS[0]).trim() || SARV_IDS[0];
  const sep = path.startsWith('/') ? '' : '/';
  return `${SARV_BASE}/${userId}${sep}${path}`;
};

const extractRecordingUrls = (call) => {
  const out = [];
  const seen = new Set();
  const push = (url, label) => {
    if (!url || typeof url !== 'string') return;
    const trimmed = url.trim();
    if (!trimmed || seen.has(trimmed)) return;
    if (!/^https?:\/\//i.test(trimmed)) return;
    seen.add(trimmed);
    out.push({ url: trimmed, label: label || `Recording ${out.length + 1}` });
  };

  const full = call?.full_row && typeof call.full_row === 'object' ? call.full_row : {};
  const sarvUserId = call?.sarv_user_id || full.sarv_user_id;
  if (call?.sf_recording_url) push(call.sf_recording_url, call.sf_recording_name || 'Tata recording');
  if (full.sf_recording_url) push(full.sf_recording_url, full.sf_recording_name || 'Tata recording');

  const recs = Array.isArray(call?.recordings)
    ? call.recordings
    : parseJson(call?.recordings, parseJson(call?.recordings_raw, parseJson(full.recordings, [])));
  if (Array.isArray(recs)) {
    recs.forEach((r, i) => {
      if (typeof r === 'string') {
        if (/^https?:\/\//i.test(r)) push(r, `Recording ${i + 1}`);
        else push(buildSarvUrl(r, sarvUserId), `Sarv recording ${i + 1}`);
      } else if (r && typeof r === 'object') {
        const directUrl = r.url || r.recording || r.recording_url || r.location;
        if (directUrl && /^https?:\/\//i.test(String(directUrl))) {
          push(directUrl, r.name || r.label);
        } else if (r.file || r.path) {
          push(buildSarvUrl(r.file || r.path, sarvUserId), r.name || r.label || `Sarv recording ${i + 1}`);
        } else if (directUrl) {
          push(buildSarvUrl(String(directUrl), sarvUserId), r.name || r.label);
        }
      }
    });
  }

  if (!out.length && recs?.[0]?.file) {
    SARV_IDS.forEach((id) => push(buildSarvUrl(recs[0].file, id), 'Sarv recording'));
  }

  return out;
};

const normalizeWfhRow = (row) => {
  const full = row?.full_row && typeof row.full_row === 'object' ? row.full_row : {};
  return {
    callid: row.sarv_call_id || row.id,
    id: row.id,
    cnumber: row.customer_number10 || full.customer_number || row.customer_number,
    createdat: row.last_hangup_time || row.received_at || row.first_answer_time,
    ctype: row.c_type || full.c_type,
    talkduration: row.talk_duration_seconds ?? full.talk_duration_seconds,
    ivrduration: row.ivr_duration_seconds ?? full.ivr_duration_seconds,
    recordings: row.recordings ?? full.recordings,
    recordings_raw: row.recordings_raw,
    sf_recording_url: full.sf_recording_url,
    sf_recording_name: full.sf_recording_name,
    sarv_user_id: full.sarv_user_id,
    telecaller_id: row.telecaller_id || full.telecaller_id,
    sf_agent_name: full.sf_agent_name,
    sf_agent_number: full.sf_agent_number,
    firstattended: full.firstattended || full.firstAttended,
    masteragent: full.masteragent || full.masterAgent,
    masteragentnumber: full.masteragentnumber || full.masterAgentNumber,
    aansh: full.aansh || full.aAnsH,
    ahdetail: full.ahdetail || full.aHDetail,
    payload: full.payload,
    full_row: full,
    source: 'wfh',
  };
};

const indexUser = (maps, user) => {
  if (!user) return;
  const name = firstNonEmpty(user.name, user.email);
  if (!name) return;
  if (user.id) maps.byUuid.set(String(user.id), name);
  if (user.call_report_id != null && user.call_report_id !== '') {
    maps.byAgentId.set(String(user.call_report_id), name);
  }
  const phone = last10(user.phone);
  if (phone.length >= 10) maps.byPhone.set(phone, name);
};

const emptyStaffMaps = () => ({
  byUuid: new Map(),
  byAgentId: new Map(),
  byPhone: new Map(),
});

const resolveStaffMaps = async (client, rows, agentUsers) => {
  const maps = emptyStaffMaps();
  (agentUsers || []).forEach((u) => indexUser(maps, u));

  const uuids = [];
  const phones = [];
  (rows || []).forEach((call) => {
    const id = staffUuidForCall(call);
    if (id && !maps.byUuid.has(id)) uuids.push(id);
    const phone = staffPhoneForCall(call);
    if (phone && !maps.byPhone.has(phone)) phones.push(phone);
  });
  const uniqueIds = [...new Set(uuids)];
  const uniquePhones = [...new Set(phones)];

  if (uniqueIds.length) {
    const { data } = await client.from('users').select('id, name, email, phone, call_report_id').in('id', uniqueIds);
    (data || []).forEach((u) => indexUser(maps, u));
  }
  for (const phone of uniquePhones) {
    if (maps.byPhone.has(phone)) continue;
    const { data } = await client
      .from('users')
      .select('id, name, email, phone, call_report_id')
      .ilike('phone', `%${phone}`)
      .limit(8);
    (data || []).forEach((u) => {
      if (last10(u.phone) === phone) indexUser(maps, u);
    });
  }
  return maps;
};

const ymdIst = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
};

const statusLabel = (code) => {
  const map = {
    3: 'Both Answered',
    4: 'Customer Answered - Agent Unanswered',
    5: 'Customer Answered',
    6: 'Customer Unanswered - Agent Answered',
    8: 'Customer Unanswered',
    10: 'Agent Answered',
  };
  return map[code] || (code == null ? 'Unknown' : `Status ${code}`);
};

const typeLabel = (type) => {
  const map = { IBD: 'Inbound', OBD: 'Outbound', CTC: 'Click to Call', QC: 'Quick Call' };
  return map[type] || type || 'Call';
};

const fmtDate = (value) => {
  if (!value) return 'N/A';
  return new Date(value).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const fmtDuration = (seconds) => {
  const n = Number(seconds);
  if (!Number.isFinite(n) || n <= 0) return '0s';
  const m = Math.floor(n / 60);
  const s = Math.floor(n % 60);
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const partyForCall = (call, customerPhones, mechanicPhones) => {
  const p = last10(call?.cnumber);
  if (p && mechanicPhones.has(p)) return 'Mechanic';
  if (p && customerPhones.has(p)) return 'Customer';
  return 'Unknown';
};

const asObject = (value) => (value && typeof value === 'object' && !Array.isArray(value) ? value : {});

const callBags = (call) => {
  const full = asObject(call?.full_row);
  const payload = asObject(full.payload || call?.payload);
  const raw = asObject(call?.rawdata || call?.rawData || full.rawdata);
  return [call, full, payload, raw, asObject(payload.answered_agent)];
};

const firstNonEmpty = (...vals) => {
  for (const v of vals) {
    if (v == null) continue;
    const s = String(v).trim();
    if (s && s !== 'null' && s !== 'undefined' && !s.startsWith('$')) return s;
  }
  return '';
};

const looksLikePersonName = (value) => {
  const s = String(value || '').trim();
  if (s.length < 2 || s.length > 80) return false;
  if (/^[\d+\s-]+$/.test(s)) return false;
  if (/^(agent|customer|unknown|roadserve|staff|n\/a)$/i.test(s)) return false;
  return /[a-zA-Z\u0900-\u097F]/.test(s);
};

const parseIdList = (value) => {
  if (value == null || value === '') return [];
  if (Array.isArray(value)) return value.map((v) => String(v).trim()).filter(Boolean);
  if (typeof value === 'object') {
    if (Array.isArray(value.ids)) return parseIdList(value.ids);
    return [];
  }
  const parsed = parseJson(value, null);
  if (Array.isArray(parsed)) return parseIdList(parsed);
  const s = String(value).trim();
  return s ? [s] : [];
};

const agentIdForCall = (call) => {
  for (const bag of callBags(call)) {
    const ids = parseIdList(bag?.aansh || bag?.aAnsH || bag?.aansh_ids);
    if (ids.length) return ids[0];
    const master = bag?.masteragent ?? bag?.masterAgent;
    if (master != null && String(master).trim() !== '') return String(master).trim();
  }
  return '';
};

const staffUuidForCall = (call) => {
  for (const bag of callBags(call)) {
    const id = firstNonEmpty(bag?.telecaller_id, bag?.user_id, bag?.staff_id);
    if (id && id.length >= 20) return id;
  }
  return '';
};

const staffPhoneForCall = (call) => {
  for (const bag of callBags(call)) {
    const p = last10(
      bag?.sf_agent_number ||
        bag?.masteragentnumber ||
        bag?.masterAgentNumber ||
        bag?.masternumctc ||
        bag?.masterNumCTC ||
        bag?.agent_number ||
        bag?.answer_agent_number ||
        bag?.answered_agent_number,
    );
    if (p.length >= 10) return p;
  }
  return '';
};

const nameFromAhDetail = (value) => {
  const rows = Array.isArray(value) ? value : parseJson(value, []);
  if (!Array.isArray(rows)) return '';
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    const name = firstNonEmpty(row.name, row.agent_name, row.agentName, row.uname, row.userName);
    if (looksLikePersonName(name)) return name;
  }
  return '';
};

const embeddedStaffName = (call) => {
  for (const bag of callBags(call)) {
    const name = firstNonEmpty(
      bag?.telecaller_name,
      bag?.agent_name,
      bag?.sf_agent_name,
      bag?.answered_agent_name,
      bag?.firstattended,
      bag?.firstAttended,
      bag?.name,
      nameFromAhDetail(bag?.ahdetail || bag?.aHDetail),
    );
    if (looksLikePersonName(name)) return name;
  }
  return '';
};

const displayStaffName = (call, maps) => {
  const embedded = embeddedStaffName(call);
  if (embedded) return embedded;
  const uuid = staffUuidForCall(call);
  if (uuid && maps.byUuid.get(uuid)) return maps.byUuid.get(uuid);
  const agentId = agentIdForCall(call);
  if (agentId && maps.byAgentId.get(agentId)) return maps.byAgentId.get(agentId);
  const phone = staffPhoneForCall(call);
  if (phone && maps.byPhone.get(phone)) return maps.byPhone.get(phone);
  if (agentId) return `Agent ${agentId}`;
  if (phone) return phone;
  return 'Unknown staff';
};

const initiatorForCall = (call, party, staffName) => {
  const type = String(call?.ctype || call?.cType || call?.c_type || '').toUpperCase();
  const who = party === 'Unknown' ? 'Number' : party;
  const staff = staffName || 'Unknown staff';
  const inbound = type === 'IBD' || type === 'INBOUND' || type === 'IB';
  if (inbound) {
    return {
      inbound: true,
      label: `${who} ne call kiya`,
      detail: `${who} → ${staff}`,
    };
  }
  return {
    inbound: false,
    label: `${staff} ne call kiya`,
    detail: `${staff} → ${who}`,
  };
};

export default function ComplaintCallRecordings({
  requestId,
  customerPhone,
  alternatePhone,
  mechanicPhone,
  mechanicAltPhone,
  registeredAt,
  cancelledAt,
  completedAt,
  embedded = false,
}) {
  const customerPhones = useMemo(() => {
    const s = new Set();
    [customerPhone, alternatePhone].forEach((p) => {
      const n = last10(p);
      if (n) s.add(n);
    });
    return s;
  }, [customerPhone, alternatePhone]);

  const mechanicPhones = useMemo(() => {
    const s = new Set();
    [mechanicPhone, mechanicAltPhone].forEach((p) => {
      const n = last10(p);
      if (n) s.add(n);
    });
    return s;
  }, [mechanicPhone, mechanicAltPhone]);

  const { data: calls = [], isLoading } = useQuery({
    queryKey: [
      'complaintCallRecordings',
      requestId,
      [...customerPhones].join(','),
      [...mechanicPhones].join(','),
      registeredAt || '',
      cancelledAt || '',
      completedAt || '',
    ],
    queryFn: async () => {
      const client = db();
      const phones = [...customerPhones, ...mechanicPhones].filter((p) => p && p.length >= 8);
      const startMs = registeredAt
        ? new Date(registeredAt).getTime() - 12 * 60 * 60 * 1000
        : Date.now() - 3 * 24 * 60 * 60 * 1000;
      const endSource = cancelledAt || completedAt;
      const endMs = endSource
        ? new Date(endSource).getTime() + 24 * 60 * 60 * 1000
        : Date.now() + 60 * 60 * 1000;
      const start = new Date(startMs).toISOString();
      const end = new Date(endMs).toISOString();

      const inWindow = (row) => {
        const t = new Date(row?.createdat || row?.created_at || 0).getTime();
        if (!Number.isFinite(t) || t <= 0) return false;
        return t >= startMs && t <= endMs;
      };

      const byId = new Map();
      const addRows = (rows) => {
        (rows || []).forEach((row) => {
          if (!inWindow(row)) return;
          const id = row.callid || row.id;
          if (!id || byId.has(id)) return;
          byId.set(id, {
            ...row,
            recordings: Array.isArray(row.recordings) ? row.recordings : parseJson(row.recordings, []),
          });
        });
      };

      const fetchByPhone = async () => {
        if (!phones.length) return;
        const orFilter = phones.map((p) => `cnumber.ilike.%${p}`).join(',');
        const { data, error } = await client
          .from('call_reports')
          .select('*')
          .or(orFilter)
          .gte('createdat', start)
          .lte('createdat', end)
          .order('createdat', { ascending: false })
          .limit(80);
        if (error) {
          for (const p of phones) {
            const res = await client
              .from('call_reports')
              .select('*')
              .ilike('cnumber', `%${p}`)
              .gte('createdat', start)
              .lte('createdat', end)
              .order('createdat', { ascending: false })
              .limit(40);
            addRows(res.data);
          }
          return;
        }
        addRows(data);
      };

      if (requestId) {
        const { data } = await client
          .from('call_reports')
          .select('*')
          .eq('service_request_id', requestId)
          .order('createdat', { ascending: false })
          .limit(80);
        addRows(data);
      }

      await fetchByPhone();

      if (phones.length) {
        const { data: wfh } = await client.rpc('admin_wfh_calls_for_phones', {
          p_phones: phones,
          p_from_date: ymdIst(start),
          p_to_date: ymdIst(end),
        });
        (wfh || []).forEach((row) => addRows([normalizeWfhRow(row)]));
      }

      const sorted = Array.from(byId.values()).sort(
        (a, b) => new Date(b.createdat || 0).getTime() - new Date(a.createdat || 0).getTime(),
      );
      const { data: agentUsers } = await client.rpc('get_agent_names_for_call_reports');
      const maps = await resolveStaffMaps(client, sorted, agentUsers || []);
      return sorted.map((call) => ({ ...call, _staffName: displayStaffName(call, maps) }));
    },
    enabled: !!requestId,
    staleTime: 20 * 1000,
    refetchInterval: 30 * 1000,
  });

  return (
    <div className={embedded ? '' : 'bg-white rounded-xl shadow-sm border border-gray-200 p-6'}>
      {!embedded ? (
        <>
          <div className="flex items-center gap-2 mb-4">
            <Phone size={20} className="text-teal-600" />
            <h2 className="text-xl font-bold text-gray-900">Call recordings</h2>
            <span className="text-sm text-gray-500">({calls.length})</span>
          </div>
          <p className="text-sm text-gray-500 mb-4">
            Customer aur mechanic dono numbers par hue calls — inbound / outbound.
          </p>
        </>
      ) : (
        <p className="text-sm text-slate-500 mb-4">
          {calls.length} call{calls.length === 1 ? '' : 's'} · customer + mechanic numbers
        </p>
      )}
      <VoiceAiCallRecording requestId={requestId} compact={embedded} />
      {isLoading ? (
        <p className="text-sm text-gray-500">Loading calls…</p>
      ) : calls.length === 0 ? (
        <p className="text-sm text-gray-500">
          {customerPhones.size || mechanicPhones.size
            ? 'Is complaint ke customer/mechanic numbers par koi call recording nahi mili.'
            : 'Call search ke liye customer ya mechanic number nahi mila.'}
        </p>
      ) : (
        <div className="space-y-4">
          {calls.map((call) => {
            const recs = extractRecordingUrls(call);
            const party = partyForCall(call, customerPhones, mechanicPhones);
            const initiated = initiatorForCall(call, party, call._staffName);
            return (
              <div key={call.callid || call.id} className="rounded-lg border border-gray-200 p-4">
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="text-sm font-semibold text-gray-900">{fmtDate(call.createdat)}</span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700">
                    {typeLabel(call.ctype || call.cType)}
                  </span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                      initiated.inbound ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'
                    }`}
                  >
                    {initiated.label}
                  </span>
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                      party === 'Mechanic'
                        ? 'bg-violet-100 text-violet-800'
                        : party === 'Customer'
                          ? 'bg-sky-100 text-sky-800'
                          : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {party}
                  </span>
                  <span className="text-xs text-gray-500">{statusLabel(call.callstatus)}</span>
                  <span className="text-xs text-gray-500">{fmtDuration(call.talkduration || call.ivrduration)}</span>
                </div>
                <p className="text-sm text-gray-700 mb-1">
                  Number: <span className="font-medium">{call.cnumber || 'N/A'}</span>
                </p>
                <p className="text-sm text-gray-700 mb-3">
                  Initiated: <span className="font-medium text-slate-900">{initiated.detail}</span>
                </p>
                {recs.length ? (
                  recs.map((r) => (
                    <CallRecordingPlayer key={r.url} src={r.url} className="w-full mb-2" showDownload />
                  ))
                ) : (
                  <p className="text-xs text-gray-400">No recording file on this call.</p>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
